import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Zap, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';

const ResetPasswordPage: FC = () => {
  const navigate        = useNavigate();
  const { token }       = useParams<{ token: string }>();
  const [password,  setPassword]  = useState('');
  const [password2, setPassword2] = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6)  { setError('Password must be at least 6 characters.'); return; }
    if (password !== password2){ setError('Passwords do not match.');                 return; }
    setLoading(true);
    try {
      const resp = await dispatch(actions.resetPassword, { password }, undefined, { token });
      if (resp?.error) { setError(resp.error.message); }
      else             { setDone(true); }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (err?: boolean) =>
    `w-full rounded-lg border px-3 py-2.5 text-sm bg-background outline-none transition-colors focus:ring-2 focus:ring-ring focus:border-transparent ${
      err ? 'border-red-400 focus:ring-red-300' : 'border-border'
    }`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        <div className="rounded-2xl border border-border bg-card shadow-card-md p-8">

          <div className="mb-8 flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
              <Zap className="h-6 w-6 text-primary" strokeWidth={2.5} />
            </div>
            <div className="text-center">
              <h1 className="text-xl font-bold text-foreground">Set new password</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">Choose a strong password</p>
            </div>
          </div>

          {done ? (
            <div className="flex flex-col items-center gap-4 py-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-center text-sm font-medium text-foreground">Password updated!</p>
              <p className="text-center text-xs text-muted-foreground">
                Your password has been reset. You can now sign in with your new password.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="mt-2 w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-all"
              >
                Sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-xs text-red-700">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">New password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'} value={password} autoFocus
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className={inputCls(!!error) + ' pr-10'}
                  />
                  <button type="button" onClick={() => setShowPw(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">Confirm password</label>
                <input
                  type="password" value={password2}
                  onChange={e => setPassword2(e.target.value)}
                  placeholder="Re-type your password"
                  className={inputCls(!!error)}
                />
              </div>

              <button
                type="submit" disabled={loading}
                className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-primary/90 disabled:opacity-60 transition-all mt-2"
              >
                {loading ? 'Updating…' : 'Reset password'}
              </button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          JumiaDash — Seller Analytics Platform
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
